import { useCaptchaConfig } from "@casino/client/context/CaptchaConfig/CaptchaConfigContext";
import useRedirectUrl from "@casino/legacy/client/components/Auth/hooks/useRedirectUrl";
import toGenericErrors from "@casino/legacy/client/components/Auth/Shared/utils/toGenericErrors";
import useFunModeToggle from "@casino/legacy/client/components/Game/GameView/GameViewDesktop/GameBlock/GameFooter/FunModeToggle/useFunModeToggle";
import updateAndOpenChatProto from "@casino/legacy/client/features/Proto/utils/updateAndOpenChatProto";
import useFeature from "@casino/legacy/client/hooks/feature/useFeature";
import useForm from "@casino/legacy/client/hooks/form/useForm";
import useRouter from "@casino/legacy/client/hooks/router/useRouter";
import useAccountPopoverStore from "@casino/legacy/client/store/useAccountPopoverStore";
import deserializeErrors from "@casino/legacy/shared/utils/errors/deserializeErrors";
import nativeReplace from "@casino/legacy/shared/utils/router/nativeReplace";
import COUNTRY_CODE from "@workspace/casino_config/cookies/COUNTRY_CODE";
import type { RegisterMutation } from "@workspace/casino_core/mutations/RegisterMutation";
import { getCookie } from "@workspace/cookies/browser";
import { useEffect, useState } from "react";
import type FormValues from "../../FormValues";

type Props = {
  isPopover?: boolean;
  onSuccess?: () => void;
};

type Payload = Partial<
  NonNullable<RegisterMutation["userManagementRegisterAsUser"]>
>;
const useHandlePayloadResponse = ({ isPopover, onSuccess }: Props) => {
  const { isRegistrationCaptchaRequired } = useCaptchaConfig();
  const [hasCaptchaRequiredError, setHasCaptchaRequiredError] = useState(
    isRegistrationCaptchaRequired,
  );
  const [loading, setLoading] = useState(false);
  const redirectUrl = useRedirectUrl();
  const { push, asPath, reload } = useRouter();
  const { dismissPopover } = useAccountPopoverStore();
  const isWalletLandingPageEnabled = useFeature("isWalletLandingPageEnabled");
  const countryCode = getCookie(COUNTRY_CODE);
  const cashierHome =
    isWalletLandingPageEnabled || countryCode === "TR"
      ? "wallet_landing"
      : "home";
  const { handleChange } = useFunModeToggle();
  const [registerSuccessful, setRegisterSuccessful] = useState(false);
  const { setError } = useForm<FormValues>({});

  useEffect(() => {
    if (registerSuccessful) {
      if (isPopover) {
        dismissPopover(true);
      }
    }
  }, [registerSuccessful, dismissPopover, isPopover]);

  const handlePayloadResponse = (
    payload: Payload | undefined,
    setErrorFromPayload: (err: GenericErrorType[]) => void,
    onPayloadSuccess?: () => void,
  ) => {
    if (payload?.errors?.length) {
      setLoading(false);
      const payloadErrors = toGenericErrors(payload.errors);
      setErrorFromPayload(payloadErrors);
      const deserializedErrors = deserializeErrors(
        payloadErrors,
      ) as DeserializeError;
      if (deserializedErrors?.captcha) {
        setHasCaptchaRequiredError(true);
      }
      if (deserializedErrors.dateOfBirth) {
        const [message] = deserializedErrors.dateOfBirth;
        setError("dateOfBirth.day", { message: `errors.${message}` });
      }
      return;
    }
    updateAndOpenChatProto();
    setRegisterSuccessful(true);
    onPayloadSuccess?.();
    onSuccess?.();
    if (isPopover) {
      handleChange(false);
      return push(`${asPath}?c=${cashierHome}`).then(() => reload());
    }
    if (asPath.includes("/loyalty")) {
      return reload();
    }
    return nativeReplace(redirectUrl);
  };
  return {
    loading,
    hasCaptchaRequiredError,
    handlePayloadResponse,
  };
};

export default useHandlePayloadResponse;
